// Phase 8.1 — Auth callback for the Emergent Google flow.
//
// Runs when the browser lands at `<origin>/#session_id=…` after
// bouncing back from `auth.emergentagent.com`. Per the playbook:
//
//   • Rendered SYNCHRONOUSLY during render (not from a useEffect) —
//     see App.js's `location.hash?.includes('session_id=')` check.
//     If we waited for useEffect, the global AuthProvider's mount
//     effect would race + call /api/auth/me → 401 → redirect to /login
//     BEFORE we'd exchanged the token.
//   • `useRef` guard so StrictMode's double-render doesn't process
//     the same session_id twice.
//   • Redirects to /login with a `?error=` query on failure so the
//     login page renders a friendly banner instead of a blank page.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

function parseSessionIdFromHash(hash) {
  if (!hash) return null;
  // Hash format: `#session_id=<token>` (Emergent uses fragment,
  // never query — the fragment stays out of server logs, which is
  // the whole point of the short-lived session_id).
  const trimmed = hash.startsWith("#") ? hash.slice(1) : hash;
  const params = new URLSearchParams(trimmed);
  return params.get("session_id");
}

export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("Finishing sign-in…");

  useEffect(() => {
    // Synchronous guard — the effect must fire at most once even
    // under React 18 StrictMode double-invocation.
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const run = async () => {
      const sessionId = parseSessionIdFromHash(window.location.hash);
      if (!sessionId) {
        navigate("/login?error=oauth_failed", { replace: true });
        return;
      }
      // Clean the URL fragment so a manual refresh doesn't retry
      // an already-consumed session_id.
      try {
        window.history.replaceState(null, "", window.location.pathname);
      } catch (_e) { /* older browsers */ }

      try {
        const { data } = await api.post("/auth/google/session", {
          session_id: sessionId,
        });
        // Warm the AuthProvider with the freshly-authenticated user
        // so the landing page doesn't have to make another /me call.
        await refresh();
        navigate("/", { replace: true, state: { user: data.user } });
      } catch (err) {
        const httpStatus = err?.response?.status;
        if (httpStatus === 403) {
          navigate("/login?error=domain_not_allowed", { replace: true });
        } else {
          navigate("/login?error=oauth_failed", { replace: true });
        }
      }
    };
    setStatus("Verifying your Google account…");
    run();
  }, [navigate, refresh]);

  return (
    <div
      className="min-h-screen flex items-center justify-center text-sm text-muted-foreground"
      data-testid="auth-callback"
    >
      {status}
    </div>
  );
}
